import { influencer } from '@round/api';
import { ExplorePlatform } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';

const PLATFORMS: ExplorePlatform[] = ['tiktok', 'instagram'];

/**
 * Identity for a selected creator. The creator explore `_id` is a Mongo internal that no endpoint
 * accepts, whereas this is the key a creator's document is upserted on, so a selection keyed on it can
 * be fetched back by id once the endpoint takes more than one (ROU-1286).
 */
export const getCreatorSelectionId = (creator: influencer.CreatorSearchResult): string | null =>
    creator.creatorbase_client_influencer_id;

/** Selection ids for a set of rows, dropping any row that carries none. */
export const getCreatorSelectionIds = (creators: influencer.CreatorSearchResult[]): string[] =>
    creators.flatMap((creator) => {
        const creatorId = getCreatorSelectionId(creator);

        return creatorId ? [creatorId] : [];
    });

export type CreatorPoolAccounts = {
    /** Platforms whose account is already in the pool. */
    memberPlatforms: ExplorePlatform[];
    /** Accounts still to add. */
    accountIdsToAdd: number[];
};

/**
 * Splits a creator's platform accounts by whether each one is already in the pool. Membership is per
 * account, so a creator can hold one account in a pool and one outside it.
 *
 * `addedAccountIds` counts accounts added since the rows were fetched, which still report the pools they
 * held at fetch time.
 */
export const getCreatorPoolAccounts = (
    creator: influencer.CreatorSearchResult,
    poolId: number | null | undefined,
    addedAccountIds: Set<number>
): CreatorPoolAccounts => {
    const memberPlatforms: ExplorePlatform[] = [];
    const accountIdsToAdd: number[] = [];

    PLATFORMS.forEach((platform) => {
        // A creator holds at most one account per platform, whatever the array shape suggests.
        const account = creator.platforms?.[platform]?.[0];
        if (!account) {
            return;
        }

        const accountId = account.creatorbase_client_influencer_account_id;
        const isInPool =
            (typeof poolId === 'number' && account.pool_ids?.includes(poolId)) || addedAccountIds.has(accountId);

        if (isInPool) {
            memberPlatforms.push(platform);
        } else {
            accountIdsToAdd.push(accountId);
        }
    });

    return { memberPlatforms, accountIdsToAdd };
};
